import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { CallEntity } from './call.entity';
import { UserEntity } from './user.entity';

/**
 * Audit row for every transfer attempt — blind, warm, or to a group.
 * Captures who initiated, what target type, the resolved target
 * identifier(s), the resulting Twilio CallSid for the transfer leg,
 * and the final outcome (`completed` / `canceled` / `failed`).
 *
 * Why a separate table: a single call can have multiple transfer
 * attempts (warm transfer that gets canceled, then a successful
 * blind transfer to a different target) — modeling on the call row
 * itself would either lose history or sprawl with optional fields.
 *
 * Used by:
 *   - Backend's `participant-join` handler — joins on
 *     `targetCallSid` to detect that a transfer-target has answered,
 *     so the rep can be removed and the transfer marked completed.
 *   - Call-history surfaces — "Transferred to Joe (Credit) at 10:42am"
 *     in the conversation timeline.
 *   - Reporting — transfer-rate per rep, transfer-acceptance-rate per
 *     target group.
 */
@Index('softphone_call_transfer_call_idx', ['fkCallId'])
@Index('softphone_call_transfer_target_call_sid_idx', ['targetCallSid'])
@Index('softphone_call_transfer_initiator_idx', ['initiatedByUserId'])
@Index('softphone_call_transfer_status_idx', ['status'])
@Entity('softphone_call_transfer')
export class SoftphoneCallTransferEntity extends BaseEntity {
	@Column('int', { name: 'fk_call__id', unsigned: true })
	public fkCallId!: number;

	/**
	 * `blind`      — rep is removed the moment the target answers.
	 * `warm`       — rep + target talk first; rep removed via explicit
	 *                `complete-warm-transfer` action (or stays if canceled).
	 * `consultAdd` — PD-14266 consultative add-to-call: the customer is
	 *                held while the rep privately hears the dialed external
	 *                party ring + pick up, then MERGES (rep stays — unlike
	 *                warm) or DROPS. Never auto-merges, so a voicemail can't
	 *                be silently bridged into the recorded conference.
	 */
	@Column('varchar', { name: 'transferKind', length: 16 })
	public transferKind!: 'blind' | 'warm' | 'consultAdd';

	/**
	 * `e164`    — external phone number (default in Twilio).
	 * `user`    — internal rep, dialed via their `client:identity`.
	 * `group`   — UserGroupEntity id; resolves to N user identities at
	 *             transfer time and rings them simultaneously (first to
	 *             accept becomes the joined participant).
	 * `aiAgent` — a ConversationAgent summoned into the call by agentId
	 *             (PD-14219 voice interop); see `targetAgentId`.
	 */
	@Column('varchar', { name: 'targetType', length: 16 })
	public targetType!: 'e164' | 'user' | 'group' | 'aiAgent';

	/** Set when targetType='e164'. */
	@Column('varchar', { name: 'targetE164', nullable: true, length: 64 })
	public targetE164!: string | null;

	/** Set when targetType='user'. */
	@Column('int', {
		name: 'targetUserId',
		nullable: true,
		unsigned: true,
	})
	public targetUserId!: number | null;

	/** Set when targetType='group'. */
	@Column('int', {
		name: 'targetGroupId',
		nullable: true,
		unsigned: true,
	})
	public targetGroupId!: number | null;

	/**
	 * Set when targetType='group' AND the destination is an RbacRole
	 * rather than a `_group` row. (Role-targeted blind transfer reuses
	 * the `'group'` targetType for the multi-target shape in
	 * `pendingTargetCallSids`; this field disambiguates so the audit
	 * preserves which destination kind was actually rung.)
	 */
	@Column('int', {
		name: 'targetRbacRoleId',
		nullable: true,
		unsigned: true,
	})
	public targetRbacRoleId!: number | null;

	/** Set when targetType='aiAgent' — the ConversationAgent summoned into
	 *  the call (PD-14219). Resolved to its summon target at transfer time. */
	@Column('int', {
		name: 'targetAgentId',
		nullable: true,
		unsigned: true,
	})
	public targetAgentId!: number | null;

	/**
	 * The Twilio CallSid returned from `participants.create()` — the
	 * leg that was dialed for the transfer target. Stays null briefly
	 * (between row insert and the REST call returning) and stays null
	 * permanently for transfers that errored before the create. Used
	 * by `handleConferenceStatus` to match `participant-join` events
	 * against in-flight transfers.
	 *
	 * For group transfers, this is null until a winner is picked —
	 * see `pendingTargetCallSids` for the per-target ringing state.
	 * On winner-joined, this is set to the winner's CallSid and the
	 * losers are cancelled.
	 */
	@Column('varchar', { name: 'targetCallSid', nullable: true, length: 64 })
	public targetCallSid!: string | null;

	/**
	 * Group-transfer-only — comma-separated list of CallSids for the
	 * N parallel `participants.create` calls fired across the group's
	 * softphone-enabled users. The participant-join handler matches a
	 * joining CallSid against this list to detect "one of the group's
	 * targets answered," picks the winner, cancels the rest, and
	 * promotes the winner's SID into `targetCallSid`.
	 *
	 * `simple-array` is a TypeORM column type that serialises
	 * `string[]` as a comma-separated TEXT field; safe for our SIDs
	 * (which never contain commas) and avoids a JSON column type
	 * which adds query complexity for what's essentially a small set.
	 */
	@Column('simple-array', {
		name: 'pendingTargetCallSids',
		nullable: true,
	})
	public pendingTargetCallSids!: ReadonlyArray<string> | null;

	/** The rep who initiated the transfer. NULL for AI-initiated transfers
	 *  (warm AI→human handoff, PD-14219) — see `initiatedByAgentId`. */
	@Column('int', { name: 'initiatedByUserId', unsigned: true, nullable: true })
	public initiatedByUserId!: number | null;

	/** Set when an AI agent initiated the transfer (AI→human warm handoff). */
	@Column('int', {
		name: 'initiatedByAgentId',
		unsigned: true,
		nullable: true,
	})
	public initiatedByAgentId!: number | null;

	@Column('datetime', { name: 'initiatedAt' })
	public initiatedAt!: Date;

	@Column('datetime', { name: 'completedAt', nullable: true })
	public completedAt!: Date | null;

	/**
	 * `initiated` — transfer underway, target ringing.
	 * `completed` — target answered (and for warm: rep dropped).
	 * `canceled`  — rep canceled before completion (warm only) OR
	 *               we proactively canceled because the target
	 *               participant already left.
	 * `failed`    — target's leg ended with no-answer/canceled/failed/
	 *               busy before joining.
	 */
	@Column('varchar', {
		name: 'status',
		length: 16,
		default: () => "'initiated'",
	})
	public status!: 'initiated' | 'completed' | 'canceled' | 'failed';

	@ManyToOne(() => CallEntity, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: 'fk_call__id', referencedColumnName: 'id' }])
	public fkCall!: CallEntity;

	@ManyToOne(() => UserEntity, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
		nullable: true,
	})
	@JoinColumn([{ name: 'initiatedByUserId', referencedColumnName: 'id' }])
	public initiatedByUser!: UserEntity | null;
}
